import { useState, useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useCompanyReports } from './useCompanyReports';
import { clearAnalysisDraft } from '../../../utils/analysisDraftStorage';
import { iMap } from '../../../components/type';
import type { SelectedCollectCollection } from '../../../utils/analysisContextLinks';
import { resolveCollectionsForAnalysisScope } from '../../../utils/analysisContextLinks';

/**
 * Hook principal pour orchestrer toute la logique de CompanyAnalysis
 * Centralise la gestion d'état et la logique métier
 */

export type AnalysisState = 'idle' | 'draft' | 'saved' | 'loading';

type PendingConfirmAction =
    | { type: 'load'; id: string; name?: string }
    | { type: 'navigation' }
    | { type: 'newAnalysis' };

interface UseCompanyAnalysisProps {
    accessToken: string;
    userId: string;
    companyId: string;
    onResetFiles?: () => void;  // Callback pour vider les documents sélectionnés
    onRestoreFiles?: (mapIds: string[], options?: { replace?: boolean }) => void | Promise<unknown>;
    onRestoreUploadedFiles?: (files: any[]) => void;
    onRestoreCollections?: (collections: SelectedCollectCollection[]) => void;
    selectedMapIds?: string[];
    selectedCollections?: SelectedCollectCollection[];
    urlAnalysisId?: string;
}

export const useCompanyAnalysis = ({
    accessToken,
    userId,
    companyId,
    onResetFiles,
    onRestoreFiles,
    onRestoreUploadedFiles,
    onRestoreCollections,
    selectedMapIds = [],
    selectedCollections = [],
    urlAnalysisId,
}: UseCompanyAnalysisProps) => {
    const pendingConfirmActionRef = useRef<PendingConfirmAction | null>(null);
    const blockNavigationRef = useRef(false);
    const loadAnalysisSeqRef = useRef(0);

    // États
    const [analysisId, setAnalysisId] = useState<string>("");
    const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showNewAnalysisModal, setShowNewAnalysisModal] = useState(false);
    const [confirmModalVariant, setConfirmModalVariant] = useState<'default' | 'leavePage'>('default');
    const [savedAnalysisName, setSavedAnalysisName] = useState<string>("");
    const [initialReportCount, setInitialReportCount] = useState<number>(0);
    const [documentsRestored, setDocumentsRestored] = useState<boolean>(false);
    const [lastSaved, setLastSaved] = useState<number>(0); // Timestamp pour forcer rechargement sidebar
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState<{
        creditsRequired: number;
        creditsAvailable: number;
        deficit: number;
        plan?: string;
    } | null>(null);
    
    // Hooks
    const { 
        reports, 
        setReports,
        isLoading, 
        submitPrompt, 
        loadAnalysis, 
        clearReports 
    } = useCompanyReports({ accessToken, userId });
    
    // Détection de modifications (après que reports soit défini)
    const hasUnsavedChanges = reports.length > initialReportCount && analysisState !== 'idle';

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return hasUnsavedChanges || blockNavigationRef.current;
    });
    const blockerRef = useRef(blocker);
    blockerRef.current = blocker;

    const openConfirmModal = useCallback((action: PendingConfirmAction) => {
        pendingConfirmActionRef.current = action;
        setConfirmModalVariant(action.type === 'navigation' ? 'leavePage' : 'default');
        setShowNewAnalysisModal(true);
    }, []);

    const clearSession = useCallback(() => {
        clearAnalysisDraft();
    }, []);

    // Effacer le brouillon à l'arrivée et au départ d'Insights (pas de reprise après navigation)
    useEffect(() => {
        clearSession();
        return () => {
            clearSession();
        };
    }, [clearSession]);

    // Nettoyer le brouillon si le contexte user/compagnie change (sans reload complet)
    useEffect(() => {
        if (!userId || !companyId) {
            clearSession();
        }
    }, [userId, companyId, clearSession]);

    /**
     * Met à jour l'état de l'analyse selon les rapports
     */
    useEffect(() => {
        if (reports.length > 0 && analysisState === 'idle') {
            setAnalysisState('draft');
        } else if (reports.length === 0 && analysisState !== 'idle' && analysisState !== 'loading') {
            setAnalysisState('idle');
        }
    }, [reports.length, analysisState]);

    /**
     * Intercepteur pour navigation externe (fermeture tab, changement URL)
     * Demande confirmation si modifications non sauvegardées
     */
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                // Chrome nécessite returnValue pour afficher le dialogue
                e.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    /**
     * Intercepteur pour navigation interne (Home, autre page Famla)
     */
    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        if (showNewAnalysisModal || showSaveModal) return;

        blockNavigationRef.current = true;
        openConfirmModal({ type: 'navigation' });
    }, [blocker.state, showNewAnalysisModal, showSaveModal, openConfirmModal]);

    /**
     * Met à jour initialReportCount quand une analyse sauvegardée est chargée
     * (fallback si le périmètre n'a pas été restauré dans handleLoadAnalysis)
     */
    useEffect(() => {
        if (analysisState === 'saved' && reports.length > 0 && !documentsRestored) {
            setInitialReportCount(reports.length);

            if (onRestoreFiles) {
                const mapIds = [...new Set(reports.map(r => r.map_id))].filter(Boolean);
                if (mapIds.length > 0) {
                    setDocumentsRestored(true);
                    void onRestoreFiles(mapIds, { replace: true });
                } else {
                    setDocumentsRestored(true);
                }
            } else {
                setDocumentsRestored(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisState, reports.length, documentsRestored, onRestoreFiles]);

    /**
     * Soumet un nouveau prompt (avec upload fichier si présent)
     */
    const handleSubmitPrompt = useCallback(async (
        promptText: string,
        selectedMaps: iMap[],
        selectedFile?: File | File[] | null,
        collectionIds?: string[],
        options?: {
            linkedProcessId?: string | null;
            synthesisOnly?: boolean;
        },
    ) => {
        const result = await submitPrompt(
            promptText,
            selectedMaps,
            analysisId,
            selectedFile,
            collectionIds,
            options,
        );
        
        if (result.success) {
            // ✅ TOUJOURS mettre à jour l'analysisId s'il est retourné
            if (result.newAnalysisId) {
                setAnalysisId(result.newAnalysisId);
            }
            setAnalysisState('draft');
        } else if (result.insufficientCredits && result.creditsData) {
            // Afficher le modal de crédits insuffisants
            setInsufficientCreditsInfo({
                creditsRequired: result.creditsData.credits_required || 0,
                creditsAvailable: result.creditsData.credits_available || 0,
                deficit: result.creditsData.deficit || 0,
                plan: result.creditsData.plan || "FREE"
            });
        }
        
        return result.success;
    }, [submitPrompt, analysisId]);

    /**
     * Charge une analyse sauvegardée (rapports + fichiers) sans vérifier les brouillons
     */
    const doLoadAnalysis = useCallback(async (loadAnalysisId: string, analysisName?: string) => {
        const seq = ++loadAnalysisSeqRef.current;
        setAnalysisState('loading');
        setDocumentsRestored(false);
        setInitialReportCount(0);
        clearReports();
        if (onResetFiles) {
            onResetFiles();
        }

        const result = await loadAnalysis(loadAnalysisId);

        if (seq !== loadAnalysisSeqRef.current) {
            return false;
        }

        if (result.reports && result.loadedReports.length > 0) {
            setReports(result.loadedReports);
            setAnalysisId(loadAnalysisId);
            setSavedAnalysisName(analysisName || "");

            const mapIds = result.scope?.map_ids?.length ? result.scope.map_ids : result.reportMapIds;
            const collectionsToRestore = await resolveCollectionsForAnalysisScope(
                result.scope,
                accessToken,
                result.reportCollectionIds,
            );

            if (seq !== loadAnalysisSeqRef.current) {
                return false;
            }

            if (onRestoreFiles) {
                await onRestoreFiles(mapIds, { replace: true });
            }

            if (seq !== loadAnalysisSeqRef.current) {
                return false;
            }

            if (onRestoreCollections) {
                onRestoreCollections(collectionsToRestore);
            }

            if (result.files && result.files.length > 0 && onRestoreUploadedFiles) {
                onRestoreUploadedFiles(result.files);
            }

            setInitialReportCount(result.reportCount ?? 0);
            setDocumentsRestored(true);
            setAnalysisState('saved');
            clearSession();
        } else {
            setAnalysisState('idle');
        }

        return result.reports;
    }, [loadAnalysis, clearSession, clearReports, setReports, onRestoreUploadedFiles, onRestoreFiles, onRestoreCollections, onResetFiles, accessToken]);

    /**
     * Charge une analyse sauvegardée (rapports + fichiers)
     */
    const handleLoadAnalysis = useCallback(async (loadAnalysisId: string, analysisName?: string) => {
        if (hasUnsavedChanges) {
            openConfirmModal({ type: 'load', id: loadAnalysisId, name: analysisName });
            return false;
        }

        pendingConfirmActionRef.current = null;
        return doLoadAnalysis(loadAnalysisId, analysisName);
    }, [hasUnsavedChanges, doLoadAnalysis, openConfirmModal]);

    /**
     * Reset complet de l'analyse
     */
    const resetAnalysis = useCallback(() => {
        clearReports();
        setAnalysisId("");
        setAnalysisState('idle');
        setSavedAnalysisName("");
        setInitialReportCount(0);
        setDocumentsRestored(false); // Reset flag documents
        clearSession();
        if (onResetFiles) {
            onResetFiles(); // Vider les documents sélectionnés
        }
    }, [clearReports, clearSession, onResetFiles]);

    /**
     * Sauvegarde l'analyse en cours
     */
    const handleSaveAnalysis = useCallback(async (name?: string) => {
        if (!analysisId || analysisId === "DEFAULT") {
            console.error('Pas d\'analyse à sauvegarder');
            return false;
        }

        // Si nom fourni, utiliser. Sinon utiliser le nom existant ou demander
        const analysisName = name || savedAnalysisName;
        if (!analysisName) {
            setShowSaveModal(true);
            return false;
        }

        try {
            const { CompanyAnalysisService } = await import('../../../services/api/companyAnalysisService');
            await CompanyAnalysisService.saveAnalysis(analysisId, analysisName, accessToken, {
                map_ids: selectedMapIds,
                collection_ids: selectedCollections.map(c => c.id),
            });

            // Recharger la sidebar avant reset (analyse visible dans « récentes »)
            setLastSaved(Date.now());
            clearSession();

            const pending = pendingConfirmActionRef.current;
            pendingConfirmActionRef.current = null;

            if (pending?.type === 'load' || pending?.type === 'navigation') {
                setTimeout(() => {
                    blockNavigationRef.current = false;
                    resetAnalysis();
                    if (pending?.type === 'load') {
                        void doLoadAnalysis(pending.id, pending.name);
                    } else if (blockerRef.current.state === 'blocked') {
                        blockerRef.current.proceed();
                    }
                }, 1500);
            } else {
                resetAnalysis();
            }

            return true;
        } catch (error: any) {
            console.error('Erreur lors de la sauvegarde:', error);
            throw error;
        }
    }, [analysisId, savedAnalysisName, accessToken, clearSession, resetAnalysis, doLoadAnalysis, selectedMapIds, selectedCollections, reports.length]);

    /**
     * Nouvelle analyse - avec confirmation si modifications non sauvegardées
     */
    const handleNewAnalysis = useCallback(() => {
        if (hasUnsavedChanges) {
            openConfirmModal({ type: 'newAnalysis' });
        } else {
            resetAnalysis();
        }
    }, [hasUnsavedChanges, resetAnalysis, openConfirmModal]);

    /**
     * Confirme la nouvelle analyse (depuis modal)
     * @param shouldSave - true: ouvrir SaveModal, false: ignorer et reset
     */
    const confirmNewAnalysis = useCallback(async (shouldSave: boolean) => {
        setShowNewAnalysisModal(false);

        if (shouldSave) {
            setShowSaveModal(true);
            return;
        }

        const pending = pendingConfirmActionRef.current;
        pendingConfirmActionRef.current = null;
        setConfirmModalVariant('default');

        if (pending?.type === 'navigation') {
            blockNavigationRef.current = false;
            resetAnalysis();
            if (blockerRef.current.state === 'blocked') {
                blockerRef.current.proceed();
            }
            return;
        }

        resetAnalysis();
        if (pending?.type === 'load') {
            await doLoadAnalysis(pending.id, pending.name);
        }
    }, [resetAnalysis, doLoadAnalysis, clearSession]);

    const cancelNewAnalysisModal = useCallback(() => {
        setShowNewAnalysisModal(false);
        pendingConfirmActionRef.current = null;
        blockNavigationRef.current = false;
        setConfirmModalVariant('default');
        if (blockerRef.current.state === 'blocked') {
            blockerRef.current.reset();
        }
    }, []);

    const cancelSaveModal = useCallback(() => {
        setShowSaveModal(false);
        pendingConfirmActionRef.current = null;
        blockNavigationRef.current = false;
        setConfirmModalVariant('default');
        if (blockerRef.current.state === 'blocked') {
            blockerRef.current.reset();
        }
    }, []);

    return {
        // États
        reports,
        isLoading,
        analysisId,
        analysisState,
        showSaveModal,
        showNewAnalysisModal,
        confirmModalVariant,
        hasUnsavedChanges,
        savedAnalysisName,
        lastSaved, // Timestamp pour forcer rechargement sidebar
        insufficientCreditsInfo, // Info pour modal crédits insuffisants
        
        // Actions
        handleSubmitPrompt,
        handleLoadAnalysis,
        handleNewAnalysis,
        handleSaveAnalysis,
        confirmNewAnalysis,
        cancelNewAnalysisModal,
        cancelSaveModal,
        resetAnalysis,
        
        // Contrôle des modals
        setShowSaveModal,
        setShowNewAnalysisModal,
        setAnalysisState,
        setSavedAnalysisName,
        setLastSaved, // Permet de mettre à jour le timestamp depuis l'extérieur
        setInsufficientCreditsInfo, // Setter pour fermer le modal de crédits insuffisants
    };
};

